import type { CountryProgress } from '@/entities/outreach-candidate';
import * as s from './CountryProgressBar.css';

export interface CountryProgressBarProps {
  /** 캠페인 `targetCountries`. 칩은 이것으로 선다 — 진행 수가 늦게 와도 나라를 고를 수 있어야 한다. */
  countries: string[];
  /** 나라별 진행. 아직 안 왔으면 칩에 수를 적지 않는다 — 「아직 안 보냄」 으로 지어내지 않는다. */
  progress?: CountryProgress[];
  /** 지금 보고 있는 나라의 초안. */
  current: string;
  onSelect: (country: string) => void;
}

const numberFormat = new Intl.NumberFormat('ko-KR');

function progressText(entry: CountryProgress | undefined): string | null {
  if (entry === undefined) return null;
  return entry.roundCount === 0
    ? '아직 안 보냄'
    : `발송 ${numberFormat.format(entry.roundCount)}회 · 폼 ${numberFormat.format(entry.formCount)}`;
}

/**
 * 나라별 진행 칩 — IA `docs/design/ia/creator-recruitment-ia.md` §3-3.
 *
 * 나라마다 `발송 N회 · 폼 M` 또는 `아직 안 보냄` 을 적고, 누르면 그 나라의 초안으로 넘어간다.
 * 선택·제외·작성 내용이 초안마다 따로 남으므로 **이 칩을 누르는 것이 곧 작업 대상을 바꾸는
 * 일**이다 — 그래서 누르는 자리를 버튼으로 두고 지금 나라를 눌린 상태로 보인다.
 *
 * **나라를 고르는 자리는 여기 하나다**(퀘스트 2f546c56 B안). 조건 칸의 「발송 나라」 를 걷었으므로
 * 나라가 하나뿐이어도 누를 수 없는 칩 하나로 그 나라를 고정해 보인다 — 조건과 같은 줄에 서서
 * 자리를 더 먹지 않는다. 나라 전환 안내는 칩의 설명(`title`)으로 갔다.
 */
export function CountryProgressBar({
  countries,
  progress,
  current,
  onSelect,
}: CountryProgressBarProps) {
  const detailOf = (country: string) =>
    progressText(progress?.find((entry) => entry.country === country));

  const only = countries.length === 1 ? countries[0] : undefined;
  if (only !== undefined) {
    const detail = detailOf(only);
    return (
      <p className={s.fixed}>
        <span className={s.country}>{only}</span>
        {detail === null ? null : <span className={s.detail}>{detail}</span>}
      </p>
    );
  }
  if (countries.length === 0) return null;

  return (
    <nav className={s.bar} aria-label="나라별 진행">
      <ul className={s.list}>
        {countries.map((country) => {
          const isCurrent = country === current;
          const detail = detailOf(country);
          return (
            <li key={country}>
              <button
                type="button"
                className={isCurrent ? s.chipCurrent : s.chip}
                aria-current={isCurrent ? 'true' : undefined}
                title="나라를 바꾸면 선택과 작성 내용이 그 나라의 초안으로 갈립니다."
                onClick={() => onSelect(country)}
              >
                <span className={s.country}>{country}</span>
                {detail === null ? null : <span className={s.detail}>{detail}</span>}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
